/**
 * ProjectBrowser - Browse project buffers organized by type
 *
 * Shows all buffers in the active project grouped as:
 * - Books (with chapters — click chapter to activate)
 * - Source Cards (archive imports)
 * - Transforms (buffers with transformedFrom)
 * - Drafts (generatedDraft metadata)
 * - Explorations (explorationOf metadata)
 * - Harvested (items from search harvests)
 *
 * @module @humanizer/studio/components/archive/ProjectBrowser
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { useBook } from '../../contexts/BookContext';
import { useStacks } from '../../contexts/StackContext';
import { useToast } from '../../contexts/ToastContext';
import { useBufferSync, cleanBufferName } from '../../contexts/BufferSyncContext';
import { BufferTimeline } from '../workspace/BufferTimeline';
import { ExportDialog } from '../workspace/ExportDialog';
import type { FindBuffer } from './CuratorView';
import type { BookData, ChapterData } from '../../types/electron';
import { createLogger } from '../../utils/debug';

const log = createLogger('ProjectBrowser');

interface ProjectBuffer {
  id: string;
  text: string;
  wordCount: number;
  state: string;
  origin: {
    metadata?: Record<string, unknown>;
    sourcePlatform?: string;
    sourceType?: string;
  };
  createdAt: number;
  updatedAt: number;
}

interface BufferGroup {
  label: string;
  items: ProjectBuffer[];
}

interface HarvestItemView {
  id: string;
  content: string;
  source?: string;
  harvestName: string;
}

function classifyBuffer(buf: ProjectBuffer): string {
  const meta = buf.origin?.metadata ?? {};
  if (meta.curatorSearch) return 'curatorSearches';
  if (meta.generatedDraft) return 'drafts';
  if (meta.explorationOf) return 'explorations';
  if (meta.transformedFrom) return 'transforms';
  return 'sources';
}

function bufferName(buf: ProjectBuffer): string {
  const meta = buf.origin?.metadata ?? {};
  // User-set title has highest priority
  const title = meta.title as string | undefined;
  if (title) return title;
  const threadTitle = meta.threadTitle as string | undefined;
  if (threadTitle) return cleanBufferName(threadTitle, buf.id.slice(0, 8));
  if (buf.text) {
    const first = buf.text.replace(/<!--[^>]*-->\n?/g, '').trim().split('\n')[0]?.trim() ?? '';
    return cleanBufferName(first, buf.id.slice(0, 8));
  }
  return buf.id.slice(0, 8);
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

interface ProjectBrowserProps {
  onBufferSelect: (bufferId: string) => void;
  /** Open a saved curator search in the workspace curator modal (M5) */
  onCuratorSearchOpen?: (findBuffer: FindBuffer) => void;
  /** Request the workspace to switch to a specific view mode */
  onViewModeRequest?: (mode: 'stack' | 'outline' | 'draft') => void;
}

export function ProjectBrowser({ onBufferSelect, onCuratorSearchOpen, onViewModeRequest }: ProjectBrowserProps) {
  const { projects, activeProject, activeProjectId, setActiveProject } = useProject();
  const {
    books, activeBookId, activeChapterId,
    setActiveBook, setActiveChapter,
    createBook, deleteBook,
    createChapter, deleteChapter,
    addCardToChapter,
    reorderChapters,
    refreshBooks,
    batchGenerating, batchProgress,
    generateAllDrafts, cancelBatchGeneration,
  } = useBook();
  const {
    stacks, activeStackId, setActiveStack,
    createStack, updateStack, deleteStack, refreshStacks,
  } = useStacks();
  const { importText } = useBufferSync();
  const { addToast } = useToast();
  const [buffers, setBuffers] = useState<ProjectBuffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; name: string; snippet: string; wordCount: number }> | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timeline and export dialog state
  const [timelineBufferId, setTimelineBufferId] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);

  // D6: Harvest items
  const [harvestItems, setHarvestItems] = useState<HarvestItemView[]>([]);

  // Book creation inline state
  const [creatingBook, setCreatingBook] = useState(false);
  const [newBookTitle, setNewBookTitle] = useState('');
  const [creatingChapterForBook, setCreatingChapterForBook] = useState<string | null>(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');

  // Stack creation / rename inline state
  const [creatingStack, setCreatingStack] = useState(false);
  const [newStackName, setNewStackName] = useState('');
  const [renamingStackId, setRenamingStackId] = useState<string | null>(null);
  const [renameStackValue, setRenameStackValue] = useState('');

  // Chapter-from-stack: which stack to copy cards from when creating a chapter
  const [chapterFromStackId, setChapterFromStackId] = useState<string | null>(null);

  // Buffer rename inline state
  const [renamingBufferId, setRenamingBufferId] = useState<string | null>(null);
  const [renameBufferValue, setRenameBufferValue] = useState('');

  // Chapter drag-to-reorder state
  const [dragChapterId, setDragChapterId] = useState<string | null>(null);
  const [dropTargetChapterId, setDropTargetChapterId] = useState<string | null>(null);

  // Load buffers
  useEffect(() => {
    if (!activeProject?.id || !window.electronBuffer?.listByProject) return;
    setLoading(true);
    window.electronBuffer.listByProject(activeProject.id)
      .then((result: any[]) => setBuffers(result))
      .catch(() => setBuffers([]))
      .finally(() => setLoading(false));
  }, [activeProject?.id]);

  // D6: Load harvest items for active project
  useEffect(() => {
    if (!activeProject?.id || !window.electronHarvest?.listHarvests) {
      setHarvestItems([]);
      return;
    }
    (async () => {
      try {
        const harvests = await window.electronHarvest!.listHarvests(activeProject.id);
        const allItems: HarvestItemView[] = [];
        for (const harvest of harvests) {
          const items = await window.electronHarvest!.listItems(harvest.id);
          for (const item of items) {
            allItems.push({
              id: item.id,
              content: item.content,
              source: item.source,
              harvestName: harvest.name,
            });
          }
        }
        setHarvestItems(allItems);
      } catch {
        setHarvestItems([]);
      }
    })();
  }, [activeProject?.id]);

  // Search (B13)
  useEffect(() => {
    if (!searchQuery.trim() || !activeProject?.id || !window.electronBuffer?.searchInProject) {
      setSearchResults(null);
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await window.electronBuffer!.searchInProject(activeProject.id, searchQuery);
        setSearchResults(results);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, activeProject?.id]);

  const handleDelete = useCallback(async (bufferId: string) => {
    if (!window.electronBuffer?.delete) return;
    await window.electronBuffer.delete(bufferId);
    setBuffers(prev => prev.filter(b => b.id !== bufferId));
  }, []);

  const handleRenameBuffer = useCallback(async () => {
    if (!renamingBufferId || !renameBufferValue.trim() || !window.electronBuffer?.rename) return;
    try {
      const updated = await window.electronBuffer.rename(renamingBufferId, renameBufferValue.trim());
      setBuffers(prev => prev.map(b => b.id === renamingBufferId ? { ...b, origin: updated.origin } : b));
      addToast('Buffer renamed', 'success');
    } catch (err) {
      log.warn('Rename failed:', err);
      addToast('Failed to rename buffer', 'warning');
    }
    setRenamingBufferId(null);
    setRenameBufferValue('');
  }, [renamingBufferId, renameBufferValue, addToast]);

  const handleTimelineRestore = useCallback(async (versionId: string) => {
    if (!window.electronBuffer?.restoreVersion || !timelineBufferId) return;
    try {
      await window.electronBuffer.restoreVersion(timelineBufferId, versionId);
      addToast('Version restored', 'success');
      setTimelineBufferId(null);
    } catch (err) {
      log.error('Restore failed:', err);
      addToast('Failed to restore version', 'warning');
    }
  }, [timelineBufferId, addToast]);

  // Harvest item click → import into workspace
  const handleHarvestItemClick = useCallback((item: HarvestItemView) => {
    importText(item.content);
  }, [importText]);

  // Chapter click → activate chapter and navigate to appropriate view
  const handleChapterClick = useCallback((book: BookData, chapter: ChapterData) => {
    setActiveBook(book.id);
    setActiveChapter(chapter.id);
    // Navigate to the right view based on chapter state
    if (onViewModeRequest) {
      if (chapter.draftBufferId) {
        onViewModeRequest('draft');
      } else {
        onViewModeRequest('stack');
      }
    }
  }, [setActiveBook, setActiveChapter, onViewModeRequest]);

  // Create book
  const handleCreateBook = useCallback(async () => {
    if (!newBookTitle.trim()) return;
    await createBook(newBookTitle.trim());
    setNewBookTitle('');
    setCreatingBook(false);
  }, [newBookTitle, createBook]);

  // Create chapter (optionally copy cards from a named stack)
  const handleCreateChapter = useCallback(async () => {
    if (!newChapterTitle.trim() || !creatingChapterForBook) return;
    setActiveBook(creatingChapterForBook);
    const chapter = await createChapter(newChapterTitle.trim());

    // B5: Copy buffer IDs from selected stack into chapter
    if (chapter && chapterFromStackId && window.electronStack) {
      try {
        const stackBuffers = await window.electronStack.listBuffers(chapterFromStackId);
        for (let i = 0; i < stackBuffers.length; i++) {
          await addCardToChapter(stackBuffers[i].bufferId, i);
        }
        addToast(`Copied ${stackBuffers.length} cards from stack`, 'success');
      } catch (err) {
        log.warn('Copy from stack failed:', err);
      }
    }

    setNewChapterTitle('');
    setCreatingChapterForBook(null);
    setChapterFromStackId(null);
  }, [newChapterTitle, creatingChapterForBook, chapterFromStackId, setActiveBook, createChapter, addCardToChapter, addToast]);

  // 5c: Convert existing project buffers into a book + chapter
  const handleConvertToBook = useCallback(async () => {
    if (!activeProject || !window.electronBook) return;
    try {
      const book = await createBook(activeProject.name || 'Untitled');
      if (!book) return;
      const chapter = await createChapter('Chapter 1');
      if (!chapter) return;
      // Add source buffers as chapter cards
      const sources = buffers.filter(b => classifyBuffer(b) === 'sources');
      for (let i = 0; i < sources.length; i++) {
        await addCardToChapter(sources[i].id, i);
      }
      addToast(`Created book with ${sources.length} cards`, 'success');
    } catch (err) {
      log.warn('Convert to book failed:', err);
      addToast('Failed to create book', 'warning');
    }
  }, [activeProject, buffers, createBook, createChapter, addCardToChapter, addToast]);

  // Stack CRUD handlers
  const handleCreateStack = useCallback(async () => {
    if (!newStackName.trim()) return;
    const stack = await createStack(newStackName.trim());
    if (stack) {
      setActiveStack(stack.id);
      addToast(`Created stack "${stack.name}"`, 'success');
    }
    setNewStackName('');
    setCreatingStack(false);
  }, [newStackName, createStack, setActiveStack, addToast]);

  const handleRenameStack = useCallback(async () => {
    if (!renamingStackId || !renameStackValue.trim()) return;
    await updateStack(renamingStackId, { name: renameStackValue.trim() });
    setRenamingStackId(null);
    setRenameStackValue('');
  }, [renamingStackId, renameStackValue, updateStack]);

  const handleDeleteStack = useCallback(async (stackId: string) => {
    await deleteStack(stackId);
    await refreshStacks();
    addToast('Stack deleted', 'success');
  }, [deleteStack, refreshStacks, addToast]);

  const handleStackClick = useCallback((stackId: string) => {
    setActiveStack(activeStackId === stackId ? null : stackId);
  }, [activeStackId, setActiveStack]);

  // Chapter drag-to-reorder handlers
  const handleChapterDragStart = useCallback((e: React.DragEvent, chapterId: string) => {
    setDragChapterId(chapterId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleChapterDragOver = useCallback((e: React.DragEvent, chapterId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (chapterId !== dragChapterId) {
      setDropTargetChapterId(chapterId);
    }
  }, [dragChapterId]);

  const handleChapterDrop = useCallback(async (e: React.DragEvent, book: BookData) => {
    e.preventDefault();
    if (!dragChapterId || !dropTargetChapterId || dragChapterId === dropTargetChapterId) {
      setDragChapterId(null);
      setDropTargetChapterId(null);
      return;
    }
    // Reorder: move dragChapterId to dropTargetChapterId's position
    const ids = book.chapters.map(c => c.id);
    const fromIdx = ids.indexOf(dragChapterId);
    const toIdx = ids.indexOf(dropTargetChapterId);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragChapterId);
    await reorderChapters(ids);
    setDragChapterId(null);
    setDropTargetChapterId(null);
    addToast('Chapters reordered', 'success');
  }, [dragChapterId, dropTargetChapterId, reorderChapters, addToast]);

  if (!activeProject) {
    return (
      <div className="project-browser__empty">
        No project active. Add cards to the stack to create one.
      </div>
    );
  }

  // Group buffers
  const grouped: Record<string, ProjectBuffer[]> = { sources: [], transforms: [], drafts: [], explorations: [], curatorSearches: [] };
  for (const buf of buffers) {
    const cat = classifyBuffer(buf);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(buf);
  }

  const groups: BufferGroup[] = [
    { label: 'Source Cards', items: grouped.sources },
    { label: 'Transforms', items: grouped.transforms },
    { label: 'Drafts', items: grouped.drafts },
    { label: 'Explorations', items: grouped.explorations },
  ].filter(g => g.items.length > 0);

  // Curator searches get their own section with special click handler
  const curatorSearches = grouped.curatorSearches;

  // D6: Harvest items shown as a separate section (not buffer-backed)
  const hasHarvestItems = harvestItems.length > 0;

  return (
    <div className="project-browser">
      <div className="project-browser__header">
        {projects.length > 1 ? (
          <select
            className="project-browser__project-select"
            value={activeProjectId ?? ''}
            onChange={(e) => setActiveProject(e.target.value || null)}
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        ) : (
          <h3 className="project-browser__project-name">{activeProject.name}</h3>
        )}
        <span className="project-browser__count">{buffers.length} buffers</span>
        {buffers.length > 0 && window.electronHarvest?.exportMarkdown && (
          <button
            className="project-browser__export-btn"
            onClick={() => setShowExport(true)}
            title="Export as Markdown"
          >
            Export
          </button>
        )}
      </div>

      {/* Search input (B13) */}
      <div className="project-browser__search">
        <input
          className="project-browser__search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search in project..."
        />
      </div>

      {/* Search results */}
      {searchResults !== null ? (
        <div className="project-browser__results">
          {searchResults.length === 0 ? (
            <div className="project-browser__empty">No results</div>
          ) : (
            searchResults.map(r => (
              <div
                key={r.id}
                className="project-browser__row"
                onClick={() => onBufferSelect(r.id)}
              >
                <span className="project-browser__row-name">{r.name}</span>
                <span className="project-browser__row-snippet">{r.snippet}</span>
                <span className="project-browser__row-words">{r.wordCount}w</span>
              </div>
            ))
          )}
        </div>
      ) : loading ? (
        <div className="project-browser__empty">Loading...</div>
      ) : (
        <>
        {/* ── STACKS SECTION ────────────────────────────────────────── */}
        {(stacks.length > 0 || window.electronStack) && (
          <div className="project-browser__group">
            <h4 className="project-browser__group-label">
              Stacks ({stacks.length})
            </h4>
            {stacks.map(stack => (
              <div
                key={stack.id}
                className={`project-browser__stack-row ${activeStackId === stack.id ? 'project-browser__stack-row--active' : ''}`}
                onClick={() => handleStackClick(stack.id)}
              >
                {renamingStackId === stack.id ? (
                  <input
                    className="project-browser__inline-input"
                    value={renameStackValue}
                    onChange={(e) => setRenameStackValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRenameStack(); if (e.key === 'Escape') setRenamingStackId(null); }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <>
                    <span className="project-browser__stack-name">
                      {activeStackId === stack.id ? '\u25BC' : '\u25B6'} {stack.name}
                    </span>
                    <span className="project-browser__stack-count">{stack.bufferCount} cards</span>
                    <button
                      className="project-browser__row-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenamingStackId(stack.id);
                        setRenameStackValue(stack.name);
                      }}
                      title="Rename stack"
                      aria-label="Rename stack"
                    >
                      &#x270E;
                    </button>
                    <button
                      className="project-browser__row-delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteStack(stack.id); }}
                      title="Delete stack"
                      aria-label="Delete stack"
                    >
                      &times;
                    </button>
                  </>
                )}
              </div>
            ))}
            {/* New Stack inline */}
            {creatingStack ? (
              <div className="project-browser__inline-create">
                <input
                  className="project-browser__inline-input"
                  value={newStackName}
                  onChange={(e) => setNewStackName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateStack(); if (e.key === 'Escape') setCreatingStack(false); }}
                  placeholder="Stack name..."
                  autoFocus
                />
              </div>
            ) : (
              <button
                className="project-browser__add-btn"
                onClick={() => setCreatingStack(true)}
              >
                + New Stack
              </button>
            )}
          </div>
        )}

        {/* ── BOOKS SECTION ────────────────────────────────────────── */}
        {(books.length > 0 || window.electronBook) && (
          <div className="project-browser__group">
            <h4 className="project-browser__group-label">
              Books ({books.length})
            </h4>
            {books.length === 0 && buffers.filter(b => classifyBuffer(b) === 'sources').length > 0 && (
              <button
                className="project-browser__add-btn project-browser__convert-btn"
                onClick={handleConvertToBook}
              >
                Convert {buffers.filter(b => classifyBuffer(b) === 'sources').length} source cards to Book
              </button>
            )}
            {books.map(book => (
              <div key={book.id} className="project-browser__book">
                <div className="project-browser__book-header">
                  <span
                    className={`project-browser__book-title ${activeBookId === book.id ? 'project-browser__book-title--active' : ''}`}
                    onClick={() => setActiveBook(activeBookId === book.id ? null : book.id)}
                  >
                    {activeBookId === book.id ? '\u25BC' : '\u25B6'} {book.title}
                  </span>
                  <span className="project-browser__count-badge">{book.chapters.length} ch</span>
                  {!batchGenerating && book.chapters.some(ch => !ch.draftBufferId) && book.chapters.length > 0 && (
                    <button
                      className="project-browser__batch-draft-btn"
                      onClick={(e) => { e.stopPropagation(); generateAllDrafts(book.id); }}
                      title="Generate drafts for all chapters without drafts"
                      aria-label="Generate all drafts"
                    >
                      Generate All
                    </button>
                  )}
                  <button
                    className="project-browser__row-delete"
                    onClick={(e) => { e.stopPropagation(); deleteBook(book.id); }}
                    title="Delete book"
                    aria-label="Delete book"
                  >
                    &times;
                  </button>
                </div>
                {activeBookId === book.id && (
                  <div
                    className="project-browser__chapters"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleChapterDrop(e, book)}
                  >
                    {batchGenerating && batchProgress && (
                      <div className="project-browser__batch-progress">
                        <span className="project-browser__batch-progress-text">
                          Drafting {batchProgress.current}/{batchProgress.total}: {batchProgress.chapterTitle}
                        </span>
                        <button
                          className="project-browser__batch-cancel-btn"
                          onClick={() => cancelBatchGeneration()}
                          aria-label="Cancel batch generation"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {book.chapters.map(ch => {
                      const hasDraft = !!ch.draftBufferId;
                      const isGenerating = batchGenerating && batchProgress?.chapterTitle === ch.title;
                      return (
                        <button
                          key={ch.id}
                          className={`project-browser__chapter ${activeChapterId === ch.id ? 'project-browser__chapter--active' : ''} ${dropTargetChapterId === ch.id ? 'project-browser__chapter--drop-target' : ''}`}
                          onClick={() => handleChapterClick(book, ch)}
                          draggable
                          onDragStart={(e) => handleChapterDragStart(e, ch.id)}
                          onDragOver={(e) => handleChapterDragOver(e, ch.id)}
                          onDragEnd={() => { setDragChapterId(null); setDropTargetChapterId(null); }}
                        >
                          <span className="project-browser__chapter-title">
                            Ch {ch.position + 1}: {ch.title}
                          </span>
                          <span className={`project-browser__chapter-draft-status ${hasDraft ? 'project-browser__chapter-draft-status--has-draft' : ''} ${isGenerating ? 'project-browser__chapter-draft-status--generating' : ''}`} title={isGenerating ? 'Generating draft...' : hasDraft ? 'Has draft' : 'Needs draft'}>
                            {isGenerating ? '\u23F3' : hasDraft ? '\u2713' : '\u2014'}
                          </span>
                          <span className="project-browser__chapter-words">{ch.wordCount}w</span>
                          {!hasDraft && onViewModeRequest && (
                            <button
                              className="project-browser__row-action project-browser__chapter-new-draft"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveBook(book.id);
                                setActiveChapter(ch.id);
                                onViewModeRequest('stack');
                              }}
                              title="Start new draft (Stack → Outline → Draft)"
                              aria-label="New draft"
                            >
                              +Draft
                            </button>
                          )}
                          <button
                            className="project-browser__row-delete"
                            onClick={(e) => { e.stopPropagation(); deleteChapter(ch.id); }}
                            title="Delete chapter"
                            aria-label="Delete chapter"
                          >
                            &times;
                          </button>
                        </button>
                      );
                    })}
                    {/* Add Chapter inline (with optional stack picker) */}
                    {creatingChapterForBook === book.id ? (
                      <div className="project-browser__inline-create">
                        <input
                          className="project-browser__inline-input"
                          value={newChapterTitle}
                          onChange={(e) => setNewChapterTitle(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCreateChapter(); if (e.key === 'Escape') { setCreatingChapterForBook(null); setChapterFromStackId(null); } }}
                          placeholder="Chapter title..."
                          autoFocus
                        />
                        {stacks.length > 0 && (
                          <select
                            className="project-browser__stack-picker"
                            value={chapterFromStackId ?? ''}
                            onChange={(e) => setChapterFromStackId(e.target.value || null)}
                          >
                            <option value="">No cards (empty chapter)</option>
                            {stacks.map(s => (
                              <option key={s.id} value={s.id}>
                                Copy from: {s.name} ({s.bufferCount} cards)
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ) : (
                      <button
                        className="project-browser__add-btn"
                        onClick={() => { setActiveBook(book.id); setCreatingChapterForBook(book.id); }}
                      >
                        + Add Chapter
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* New Book inline */}
            {creatingBook ? (
              <div className="project-browser__inline-create">
                <input
                  className="project-browser__inline-input"
                  value={newBookTitle}
                  onChange={(e) => setNewBookTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateBook(); if (e.key === 'Escape') setCreatingBook(false); }}
                  placeholder="Book title..."
                  autoFocus
                />
              </div>
            ) : (
              <button
                className="project-browser__add-btn"
                onClick={() => setCreatingBook(true)}
              >
                + New Book
              </button>
            )}
          </div>
        )}

        {groups.map(group => (
          <div key={group.label} className="project-browser__group">
            <h4 className="project-browser__group-label">
              {group.label}
              <span className="project-browser__count-badge">{group.items.length}</span>
            </h4>
            {group.items.map(buf => (
              <div
                key={buf.id}
                className="project-browser__row"
                onClick={() => onBufferSelect(buf.id)}
              >
                {renamingBufferId === buf.id ? (
                  <input
                    className="project-browser__inline-input"
                    value={renameBufferValue}
                    onChange={(e) => setRenameBufferValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRenameBuffer();
                      if (e.key === 'Escape') { setRenamingBufferId(null); setRenameBufferValue(''); }
                    }}
                    onBlur={() => handleRenameBuffer()}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <span
                    className="project-browser__row-name"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (window.electronBuffer?.rename) {
                        setRenamingBufferId(buf.id);
                        setRenameBufferValue(bufferName(buf));
                      }
                    }}
                    title="Double-click to rename"
                  >
                    {bufferName(buf)}
                  </span>
                )}
                <span className="project-browser__row-words">{buf.wordCount.toLocaleString()}w</span>
                <span className="project-browser__row-state">{buf.state}</span>
                <span className="project-browser__row-time">{relativeTime(buf.updatedAt)}</span>
                {window.electronBuffer?.getTimeline && (
                  <button
                    className="project-browser__row-history"
                    onClick={(e) => { e.stopPropagation(); setTimelineBufferId(buf.id); }}
                    title="View history"
                    aria-label="View history"
                  >
                    &#x23F1;
                  </button>
                )}
                {window.electronBuffer?.rename && (
                  <button
                    className="project-browser__row-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenamingBufferId(buf.id);
                      setRenameBufferValue(bufferName(buf));
                    }}
                    title="Rename buffer"
                    aria-label="Rename buffer"
                  >
                    &#x270E;
                  </button>
                )}
                <button
                  className="project-browser__row-delete"
                  onClick={(e) => { e.stopPropagation(); handleDelete(buf.id); }}
                  title="Delete"
                  aria-label="Delete buffer"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        ))}
        {/* M5: Curator Searches section */}
        {curatorSearches.length > 0 && (
          <div className="project-browser__group">
            <h4 className="project-browser__group-label">Curator Searches ({curatorSearches.length})</h4>
            {curatorSearches.map(buf => {
              const meta = buf.origin?.metadata ?? {};
              const query = (meta.curatorQuery as string) ?? '';
              const anchor = (meta.curatorAnchor as string) ?? '';
              const count = (meta.resultCount as number) ?? 0;
              return (
                <div
                  key={buf.id}
                  className="project-browser__row"
                  onClick={() => {
                    if (onCuratorSearchOpen) {
                      // Build a FindBuffer from the saved metadata and reopen
                      const fb: FindBuffer = {
                        id: crypto.randomUUID(),
                        query,
                        anchorPassage: anchor,
                        results: [], // Will be re-searched on open
                        timestamp: meta.searchTimestamp as number ?? buf.createdAt,
                        parentFindBufferId: meta.parentFindBufferId as string | undefined,
                        savedBufferId: buf.id,
                      };
                      onCuratorSearchOpen(fb);
                    } else {
                      onBufferSelect(buf.id);
                    }
                  }}
                >
                  <span className="project-browser__row-name">
                    {query.slice(0, 50)}{query.length > 50 ? '...' : ''}
                  </span>
                  <span className="project-browser__row-words">{count} results</span>
                  <span className="project-browser__row-time">{relativeTime(buf.updatedAt)}</span>
                  <button
                    className="project-browser__row-delete"
                    onClick={(e) => { e.stopPropagation(); handleDelete(buf.id); }}
                    title="Delete"
                    aria-label="Delete curator search"
                  >
                    &times;
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {/* D6: Harvest items section — now clickable */}
        {hasHarvestItems && (
          <div className="project-browser__group">
            <h4 className="project-browser__group-label">Harvested ({harvestItems.length})</h4>
            {harvestItems.map(item => (
              <button
                key={item.id}
                className="project-browser__row project-browser__row--harvest"
                onClick={() => handleHarvestItemClick(item)}
              >
                <span className="project-browser__row-name">
                  {item.content.slice(0, 60).replace(/\n/g, ' ')}{item.content.length > 60 ? '...' : ''}
                </span>
                {item.source && (
                  <span className="project-browser__row-state">{item.source.replace('curator:', '').slice(0, 8)}</span>
                )}
                <span className="project-browser__row-words">{item.harvestName}</span>
              </button>
            ))}
          </div>
        )}
        </>
      )}

      {/* Timeline overlay */}
      {timelineBufferId && (
        <div className="project-browser__timeline-overlay">
          <BufferTimeline
            bufferId={timelineBufferId}
            onRestore={handleTimelineRestore}
            onClose={() => setTimelineBufferId(null)}
          />
        </div>
      )}

      {/* Export dialog */}
      {showExport && activeProject && (
        <ExportDialog
          projectId={activeProject.id}
          projectName={activeProject.name}
          bufferCount={buffers.length}
          onClose={() => setShowExport(false)}
          onExportComplete={(path) => addToast(`Exported to ${path}`, 'success')}
        />
      )}
    </div>
  );
}
